import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SECTION_META = {
  pf: { title: 'Provident Fund (PF)', icon: 'Shield', color: 'text-blue-600' },
  esi: { title: 'Employee State Insurance (ESI)', icon: 'Heart', color: 'text-green-600' },
  tds: { title: 'Tax Deducted at Source (TDS)', icon: 'Calculator', color: 'text-purple-600' },
};

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

function loadChallanState(period) {
  try {
    const raw = localStorage.getItem(`payroll_challans_${period}`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveChallanState(period, state) {
  localStorage.setItem(`payroll_challans_${period}`, JSON.stringify(state));
}

function buildChallanText(type, data, period) {
  const label = SECTION_META[type]?.title || type.toUpperCase();
  return [
    `${label} Challan Summary`,
    `Period: ${period}`,
    `Generated: ${new Date().toLocaleString('en-IN')}`,
    '',
    `Employee Contribution: ${formatCurrency(data.employeeContribution)}`,
    `Employer Contribution: ${formatCurrency(data.employerContribution)}`,
    `Total Amount: ${formatCurrency(data.totalAmount)}`,
    `Due Date: ${data.dueDate || 'N/A'}`,
  ].join('\n');
}

function downloadText(filename, content) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function StatutoryComplianceSidebar({
  complianceData,
  selectedPeriod,
  payrollRecordCount = 0,
  onChallanGenerated,
}) {
  const navigate = useNavigate();
  const [isMinimized, setIsMinimized] = useState(() => {
    return localStorage.getItem('payroll_compliance_minimized') === 'true';
  });
  const [expandedSections, setExpandedSections] = useState({ pf: true, esi: false, tds: false });
  const [challanState, setChallanState] = useState(() => loadChallanState(selectedPeriod));
  const [viewSection, setViewSection] = useState(null);

  useEffect(() => {
    setChallanState(loadChallanState(selectedPeriod));
  }, [selectedPeriod]);

  const deadlines = useMemo(() => {
    const [year, month] = (selectedPeriod || '').split('-').map(Number);
    if (!year || !month) return [];
    const monthName = new Date(year, month - 1, 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
    return [
      { label: 'PF challan', date: `15 ${monthName.split(' ')[0]} ${year}`, type: 'pf' },
      { label: 'ESI challan', date: `21 ${monthName.split(' ')[0]} ${year}`, type: 'esi' },
      { label: 'TDS deposit', date: `7 ${monthName.split(' ')[0]} ${year}`, type: 'tds' },
    ];
  }, [selectedPeriod]);

  const toggleMinimize = () => {
    const next = !isMinimized;
    setIsMinimized(next);
    localStorage.setItem('payroll_compliance_minimized', String(next));
  };

  const getEffectiveStatus = (type, data) => {
    if (challanState[type] === 'generated') return 'generated';
    return data?.challanStatus || 'pending';
  };

  const handleGenerateChallan = (type) => {
    const data = complianceData[type];
    if (!data || payrollRecordCount === 0) return;

    downloadText(`${type}-challan-${selectedPeriod}.txt`, buildChallanText(type, data, selectedPeriod));
    const next = { ...challanState, [type]: 'generated' };
    setChallanState(next);
    saveChallanState(selectedPeriod, next);
    onChallanGenerated?.(type);
  };

  const handleGenerateAll = () => {
    ['pf', 'esi', 'tds'].forEach((type) => {
      if (complianceData[type]?.totalAmount > 0 && challanState[type] !== 'generated') {
        handleGenerateChallan(type);
      }
    });
  };

  const handleExportReport = () => {
    const lines = [
      'Statutory Compliance Report',
      `Period: ${selectedPeriod}`,
      `Payroll Records: ${payrollRecordCount}`,
      '',
    ];
    Object.entries(SECTION_META).forEach(([key, meta]) => {
      const d = complianceData[key];
      lines.push(`${meta.title}`);
      lines.push(`  Employee: ${formatCurrency(d?.employeeContribution)}`);
      lines.push(`  Employer: ${formatCurrency(d?.employerContribution)}`);
      lines.push(`  Total: ${formatCurrency(d?.totalAmount)}`);
      lines.push(`  Status: ${getEffectiveStatus(key, d)}`);
      lines.push('');
    });
    downloadText(`compliance-report-${selectedPeriod}.txt`, lines.join('\n'));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'generated':
        return 'text-green-700 bg-green-100 border-green-200';
      case 'overdue':
        return 'text-red-700 bg-red-100 border-red-200';
      default:
        return 'text-amber-700 bg-amber-100 border-amber-200';
    }
  };

  if (isMinimized) {
    return (
      <div className="w-12 shrink-0">
        <button
          type="button"
          onClick={toggleMinimize}
          className="sticky top-24 w-12 bg-card border border-border rounded-lg p-2 flex flex-col items-center gap-2 hover:bg-muted/50 transition-colors"
          title="Expand Statutory Compliance"
        >
          <Icon name="FileCheck" size={18} className="text-primary" />
          <Icon name="ChevronLeft" size={14} className="text-muted-foreground" />
          <span className="text-[10px] text-muted-foreground writing-mode-vertical" style={{ writingMode: 'vertical-rl' }}>
            Compliance
          </span>
        </button>
      </div>
    );
  }

  const grandTotal = (complianceData.pf?.totalAmount || 0)
    + (complianceData.esi?.totalAmount || 0)
    + (complianceData.tds?.totalAmount || 0);

  return (
    <>
      <div className="w-72 xl:w-80 bg-card border border-border rounded-lg h-fit sticky top-24">
        <div className="p-4 border-b border-border flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
              <Icon name="FileCheck" size={18} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-foreground text-sm truncate">Statutory Compliance</h3>
              <p className="text-xs text-muted-foreground">{selectedPeriod} · {payrollRecordCount} records</p>
            </div>
          </div>
          <button
            type="button"
            onClick={toggleMinimize}
            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground"
            title="Minimize panel"
          >
            <Icon name="ChevronRight" size={18} />
          </button>
        </div>

        <div className="p-4 border-b border-border bg-muted/30">
          <p className="text-xs text-muted-foreground">Total statutory liability</p>
          <p className="text-xl font-bold text-foreground">{formatCurrency(grandTotal)}</p>
          {payrollRecordCount === 0 && (
            <p className="text-xs text-amber-700 mt-1">Add payroll records to calculate amounts.</p>
          )}
        </div>

        <div className="p-4 space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto">
          {Object.entries(SECTION_META).map(([id, meta]) => {
            const data = complianceData[id] || {};
            const status = getEffectiveStatus(id, data);
            const isOpen = expandedSections[id];

            return (
              <div key={id} className="border border-border rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedSections((p) => ({ ...p, [id]: !p[id] }))}
                  className="w-full p-3 flex items-center justify-between hover:bg-muted/40 text-left"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon name={meta.icon} size={16} className={meta.color} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{id.toUpperCase()}</p>
                      <p className="text-xs text-muted-foreground">{formatCurrency(data.totalAmount)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${getStatusColor(status)}`}>
                      {status}
                    </span>
                    <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={14} />
                  </div>
                </button>

                {isOpen && (
                  <div className="px-3 pb-3 border-t border-border text-xs space-y-2 pt-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Employee</span>
                      <span>{formatCurrency(data.employeeContribution)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Employer</span>
                      <span>{formatCurrency(data.employerContribution)}</span>
                    </div>
                    {data.dueDate && (
                      <div className="flex justify-between text-muted-foreground">
                        <span>Due</span>
                        <span>{data.dueDate}</span>
                      </div>
                    )}
                    <div className="flex gap-1.5 pt-1">
                      <Button
                        variant="outline"
                        size="xs"
                        className="flex-1"
                        disabled={status === 'generated' || payrollRecordCount === 0}
                        onClick={() => handleGenerateChallan(id)}
                      >
                        Generate
                      </Button>
                      <Button variant="ghost" size="xs" className="flex-1" onClick={() => setViewSection(id)}>
                        View
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <div className="space-y-1.5 pt-2">
            <Button variant="outline" size="sm" className="w-full justify-start text-xs" onClick={handleGenerateAll} disabled={payrollRecordCount === 0}>
              <Icon name="Download" size={14} className="mr-2" />
              Download All Challans
            </Button>
            <Button variant="outline" size="sm" className="w-full justify-start text-xs" onClick={handleExportReport}>
              <Icon name="FileSpreadsheet" size={14} className="mr-2" />
              Export Report
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start text-xs"
              onClick={() => navigate('/erp/compliance-statutory-reports')}
            >
              <Icon name="Calendar" size={14} className="mr-2" />
              Compliance Calendar
            </Button>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs font-medium text-amber-900 mb-2">Upcoming Deadlines</p>
            <ul className="space-y-1">
              {deadlines.map((d) => (
                <li key={d.type} className="text-xs text-amber-800 flex justify-between gap-2">
                  <span>{d.label}</span>
                  <span className="font-medium">{d.date}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {viewSection && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card border border-border rounded-xl w-full max-w-sm shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-semibold">{SECTION_META[viewSection]?.title}</h3>
              <button type="button" onClick={() => setViewSection(null)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              {['employeeContribution', 'employerContribution', 'totalAmount'].map((key) => (
                <div key={key} className="flex justify-between">
                  <span className="text-muted-foreground capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                  <span className="font-medium">{formatCurrency(complianceData[viewSection]?.[key])}</span>
                </div>
              ))}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(getEffectiveStatus(viewSection, complianceData[viewSection]))}`}>
                  {getEffectiveStatus(viewSection, complianceData[viewSection])}
                </span>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="default"
                  size="sm"
                  className="flex-1"
                  disabled={getEffectiveStatus(viewSection, complianceData[viewSection]) === 'generated'}
                  onClick={() => { handleGenerateChallan(viewSection); setViewSection(null); }}
                >
                  Generate Challan
                </Button>
                <Button variant="outline" size="sm" className="flex-1" onClick={() => setViewSection(null)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
